import { PM_REACT_MESSAGING_STYLES } from "./styles/pmReactStyles";

import React from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  PmReactMessagingApp,
  type PmReactMessagingProps,
} from "./PmReactMessagingApp";

type PmReactMessagingApi = {
  version: string;
  mount: (el: Element, props: PmReactMessagingProps) => void;
  update: (el: Element, props: PmReactMessagingProps) => void;
  unmount: (el: Element) => void;
};

type ShadowMount = {
  root: Root;
  shadowRoot: ShadowRoot;
  container: HTMLDivElement;
};

const mounts = new WeakMap<Element, ShadowMount>();

/**
 * Create a Shadow DOM boundary for complete style isolation.
 * This prevents Ionic/Angular global styles from affecting React content.
 */
const createShadowMount = (el: Element): ShadowMount => {
  const anyEl = el as any;
  const shadowRoot: ShadowRoot =
    (el as Element & { shadowRoot?: ShadowRoot }).shadowRoot ||
    (typeof anyEl.attachShadow === "function"
      ? anyEl.attachShadow({ mode: "open" })
      : (() => {
          throw new Error(
            "Shadow DOM is not supported by this browser. PmReact.messaging requires Shadow DOM for style isolation.",
          );
        })());

  // If the element already had a shadowRoot (e.g., mount → unmount → mount),
  // clear it so we don't accumulate duplicate style/container nodes.
  while (shadowRoot.firstChild) shadowRoot.removeChild(shadowRoot.firstChild);

  // Inject our scoped CSS into the shadow root
  const styleEl = document.createElement("style");
  styleEl.textContent = PM_REACT_MESSAGING_STYLES;
  shadowRoot.appendChild(styleEl);

  // Create a container for React inside the shadow root
  const container = document.createElement("div");
  container.className = "pm-react-shadow-container";
  container.style.height = "100%";
  container.style.width = "100%";
  shadowRoot.appendChild(container);

  // Create React root inside shadow DOM
  const root = createRoot(container);

  return { root, shadowRoot, container };
};

const render = (el: Element, props: PmReactMessagingProps) => {
  let mount = mounts.get(el);

  if (!mount) {
    mount = createShadowMount(el);
    mounts.set(el, mount);
  }

  mount.root.render(<PmReactMessagingApp {...props} />);
};

const unmount = (el: Element) => {
  const mount = mounts.get(el);
  if (!mount) return;
  mount.root.unmount();
  mounts.delete(el);

  // Optional cleanup: remove injected nodes to keep DOM tidy.
  // Note: the ShadowRoot remains attached; it cannot be detached.
  const shadowRoot = (el as any).shadowRoot as ShadowRoot | undefined;
  if (shadowRoot) shadowRoot.innerHTML = "";
};

const api: PmReactMessagingApi = {
  version: "0.0.0",
  mount: render,
  update: render,
  unmount,
};

const w = typeof window !== "undefined" ? (window as any) : null;
if (w) {
  w.PmReact = w.PmReact || {};
  w.PmReact.messaging = api;
}
